// ============================================================
// MANDOLIN THEORY ENGINE
// Tuning: G3=7, D4=2, A4=9, E5=4  (CHROMATIC semitone indices)
// String indices: 0=G (lowest), 1=D, 2=A, 3=E (highest)
// ============================================================

import { noteIndex, usesFlats, getNoteName } from './theory';

export const MANDOLIN_TUNING = [7, 2, 9, 4]; // G, D, A, E
export const TOTAL_FRETS_MANDOLIN = 20;

export const MANDOLIN_INLAY_FRETS = [3, 5, 7, 10, 12, 15, 17];
export const MANDOLIN_DOUBLE_INLAY_FRETS = [12];

// ── FFcP scale formulas ──────────────────────────────────────────────────────
const FFCP_FORMULAS = {
  'STANDARD SCALE': { major: [0, 2, 4, 5, 7, 9, 11], minor: [0, 2, 3, 5, 7, 8, 10] },
  'MAJOR SCALE':    { major: [0, 2, 4, 5, 7, 9, 11], minor: [0, 2, 3, 5, 7, 8, 10] },
  'PENTATONIC':     { major: [0, 2, 4, 7, 9],         minor: [0, 3, 5, 7, 10] },
  'TRIADS':         { major: [0, 4, 7],                minor: [0, 3, 7] },
  // Diatonic modes — fixed interval sets, tonality not applicable
  'IONIAN':         { major: [0, 2, 4, 5, 7, 9, 11], minor: [0, 2, 4, 5, 7, 9, 11] },
  'DORIAN':         { major: [0, 2, 3, 5, 7, 9, 10], minor: [0, 2, 3, 5, 7, 9, 10] },
  'PHRYGIAN':       { major: [0, 1, 3, 5, 7, 8, 10], minor: [0, 1, 3, 5, 7, 8, 10] },
  'LYDIAN':         { major: [0, 2, 4, 6, 7, 9, 11], minor: [0, 2, 4, 6, 7, 9, 11] },
  'MIXOLYDIAN':     { major: [0, 2, 4, 5, 7, 9, 10], minor: [0, 2, 4, 5, 7, 9, 10] },
  'AEOLIAN':        { major: [0, 2, 3, 5, 7, 8, 10], minor: [0, 2, 3, 5, 7, 8, 10] },
  'LOCRIAN':        { major: [0, 1, 3, 5, 6, 8, 10], minor: [0, 1, 3, 5, 6, 8, 10] },
};

// ── FFcP finger position bounds ───────────────────────────────────────────────
// max: fret offset above root at which the algorithm jumps to the next higher string
// (Adjacent strings are a P5 = 7 semitones apart; the algorithm subtracts 7 per jump.)
const FFCP_FINGER_BOUNDS = {
  1: 5, // 1st Finger (Index):  root + up to 5 frets before jumping string
  2: 4, // 2nd Finger (Middle): root + up to 4 frets
  3: 2, // 3rd Finger (Ring):   root + up to 2 frets
  4: 0, // 4th Finger (Pinky):  anything above root immediately jumps string
};

const FFCP_FINGER_NAMES = {
  '1st FINGER': 1,
  '2nd FINGER': 2,
  '3rd FINGER': 3,
  '4th FINGER': 4,
};

// ── Chop chord shape definitions ─────────────────────────────────────────────
// offsets: [G-string, D-string, A-string, E-string] fret offsets from anchor fret
// anchor: string index that carries the root note
// null offset = string is statically muted for this shape (C SHAPE E-string: 4-string
// stretch is physically too severe, so it is always a 3-note truncated voicing).
const CHOP_SHAPES = {
  'G SHAPE': { anchor: 1, offsets: [ 2,  0, -3, -2] }, // Root on D string ("Big Chop")
  'A SHAPE': { anchor: 0, offsets: [ 0,  0,  2,  3] }, // Root on G string
  'C SHAPE': { anchor: 0, offsets: [ 0, -3, -2, null] }, // Root on G string, E muted
  'D SHAPE': { anchor: 2, offsets: [ 2, -1,  0, -3] }, // Root on A string ("Saddle")
};

// ── Double-stop fret deltas ───────────────────────────────────────────────────
// Delta = upper-string fret minus lower-string fret, for adjacent string pairs
// (Adjacent strings are tuned a P5 apart = 7 semitones, so same fret = P5.)
const DOUBLE_STOP_DELTAS = {
  '3rds': [-4, -3], // major 3rd (−4) or minor 3rd (−3)
  '4ths': [-2],
  '5ths': [0],
  '6ths': [2],
};

// ── Builder: FFcP ─────────────────────────────────────────────────────────────
/**
 * Returns [{ string, fret, isRoot, interval, noteName }] for scale tones.
 *
 * When shapeName is null/undefined (Standard Scales mode): returns the full-neck
 * open pattern across all frets and strings.
 *
 * When shapeName is '1st FINGER' | '2nd FINGER' | '3rd FINGER' | '4th FINGER':
 * applies FFcP positional windowing — each scale degree is mapped to a single
 * fret/string position anchored at the root on the G string, with string hops
 * determined by the finger's bounding box (FFCP_FINGER_BOUNDS).
 */
export function buildFFcPNodes(rootName, mode, tonality, stringFocus, shapeName) {
  const root = noteIndex(rootName);
  if (root === -1) return [];

  const formula =
    FFCP_FORMULAS[mode]?.[tonality] ??
    FFCP_FORMULAS['STANDARD SCALE'].major;

  const flats = usesFlats(rootName, formula, tonality);

  // ── Full-neck mode (Standard Scales or no shape selected) ──────────────────
  if (!shapeName || !FFCP_FINGER_NAMES[shapeName]) {
    const formulaSet = new Set(formula);
    const nodes = [];
    for (let s = 0; s < 4; s++) {
      if (!stringFocus[s]) continue;
      for (let f = 0; f <= TOTAL_FRETS_MANDOLIN; f++) {
        const pitch    = (MANDOLIN_TUNING[s] + f) % 12;
        const interval = (pitch - root + 12) % 12;
        if (formulaSet.has(interval)) {
          nodes.push({
            string:   s,
            fret:     f,
            isRoot:   interval === 0,
            interval,
            noteName: getNoteName(pitch, flats),
          });
        }
      }
    }
    return nodes;
  }

  // ── FFcP positional window mode ────────────────────────────────────────────
  // Algorithm (adapted from calculateMovableScale in FFcP Fretboard Logic):
  //   Each scale degree starts on the G string (string 0). While the fret offset
  //   above the root exceeds the finger's max bound, hop to the next higher-pitched
  //   string and subtract 7 (one perfect fifth = one string's worth of tuning).
  //
  // String indices: 0=G (lowest), 1=D, 2=A, 3=E (highest)
  // Adjacent strings are tuned a P5 apart = 7 semitones.

  const fingerNum = FFCP_FINGER_NAMES[shapeName];
  const maxDelta  = FFCP_FINGER_BOUNDS[fingerNum];

  // Root fret on G string — prefer a fretted position over an open string
  let rootFret = ((root - MANDOLIN_TUNING[0]) % 12 + 12) % 12;
  if (rootFret === 0) rootFret = 12;

  const nodes = [];
  for (const interval of formula) {
    let s      = 0; // start on G string
    let deltaF = interval;

    // Hop to successively higher strings while the offset exceeds the bound
    while (deltaF > maxDelta && s < 3) {
      s++;
      deltaF -= 7;
    }

    const fret = rootFret + deltaF;
    if (fret < 0 || fret > TOTAL_FRETS_MANDOLIN) continue;
    if (!stringFocus[s]) continue;

    const pitch = (MANDOLIN_TUNING[s] + fret) % 12;
    nodes.push({
      string:   s,
      fret,
      isRoot:   interval === 0,
      interval,
      noteName: getNoteName(pitch, flats),
    });
  }
  return nodes;
}

// ── Builder: Chop Chords ──────────────────────────────────────────────────────
/**
 * Returns one node per active string.
 *
 * Non-muted strings: { string, fret, isMuted: false, isRoot, interval, noteName }
 * Muted strings:     { string, fret: null, isMuted: true, isRoot: false, interval: null, noteName: null }
 *
 * A string is muted when:
 *   (a) the shape defines a null offset (static shape mute, e.g. C SHAPE E-string), OR
 *   (b) the computed fret is 0 — open strings are forbidden in bluegrass chop chords
 *       because the required staccato mute is impossible on an open string, OR
 *   (c) the Stretch Factor (maxFret − minFret across active strings) exceeds the hand-span
 *       limit (5 for major, 6 for minor) after applying the minor-3rd flat — in that case
 *       the highest-pitched active string is gracefully dropped to preserve playability.
 */
export function buildChopChordNodes(rootName, shapeName, tonality, stringFocus) {
  const root = noteIndex(rootName);
  if (root === -1) return [];

  const flats = usesFlats(rootName, null, tonality);
  const shape = CHOP_SHAPES[shapeName];
  if (!shape) return [];

  const { anchor, offsets } = shape;

  // Exclude static null offsets from range calculations
  const validOffsets = offsets.filter(o => o !== null);
  const minOffset    = Math.min(...validOffsets);
  const maxOffset    = Math.max(...validOffsets);

  // Canonical (lowest) fret where root appears on the anchor string
  let anchorFret = ((root - MANDOLIN_TUNING[anchor]) % 12 + 12) % 12;

  // Shift up until all valid offsets produce non-negative frets
  while (anchorFret + minOffset < 0) anchorFret += 12;
  // Pull back if the top of the shape exceeds the fretboard
  if (anchorFret + maxOffset > TOTAL_FRETS_MANDOLIN) anchorFret -= 12;

  // ── Step 1: apply shape offsets (null → already muted) ──────────────────────
  const frets = offsets.map(offset => offset === null ? null : anchorFret + offset);

  // ── Step 2: flat major 3rds for minor tonality ───────────────────────────────
  // Keep this logic exactly — it is mathematically correct.
  for (let s = 0; s < 4; s++) {
    if (frets[s] === null) continue;
    if (tonality === 'minor') {
      const pitch    = (MANDOLIN_TUNING[s] + frets[s]) % 12;
      const interval = (pitch - root + 12) % 12;
      if (interval === 4) frets[s] -= 1; // major 3rd → minor 3rd
    }
  }

  // ── Step 3 (Phase 2): ban open strings ───────────────────────────────────────
  // fret === 0 is physically invalid for a chop chord; mute such strings.
  for (let s = 0; s < 4; s++) {
    if (frets[s] !== null && frets[s] <= 0) frets[s] = null;
  }

  // ── Step 4 (Phase 3): Stretch Factor limit ───────────────────────────────────
  // If the active frets span more than the hand can reach, gracefully drop the
  // highest-pitched active string to create a playable 3-note voicing.
  const activeFretValues = frets.filter(f => f !== null);
  if (activeFretValues.length >= 2) {
    const maxF    = Math.max(...activeFretValues);
    const minF    = Math.min(...activeFretValues);
    const stretch = maxF - minF;
    const limit   = tonality === 'minor' ? 6 : 5;

    if (stretch > limit) {
      // Drop the highest-pitched active string (highest index = highest pitch)
      for (let s = 3; s >= 0; s--) {
        if (frets[s] !== null) {
          frets[s] = null;
          break;
        }
      }
    }
  }

  // ── Build output nodes ────────────────────────────────────────────────────────
  const nodes = [];
  for (let s = 0; s < 4; s++) {
    if (!stringFocus[s]) continue;

    if (frets[s] === null) {
      nodes.push({ string: s, fret: null, isMuted: true, isRoot: false, interval: null, noteName: null });
      continue;
    }

    const fret     = frets[s];
    const pitch    = (MANDOLIN_TUNING[s] + fret) % 12;
    const interval = (pitch - root + 12) % 12;
    nodes.push({
      string:   s,
      fret,
      isMuted:  false,
      isRoot:   interval === 0,
      interval,
      noteName: getNoteName(pitch, flats),
    });
  }
  return nodes;
}

// ── Builder: Double Stops ─────────────────────────────────────────────────────
/**
 * Shows scale-tone positions that form valid double-stop pairs on adjacent string
 * pairs (G-D, D-A, A-E) for the chosen interval type.
 *
 * Fret delta between lower and upper string (adjacent strings = P5 = 7 semitones):
 *   5ths  → delta  0  (same fret)
 *   4ths  → delta −2
 *   3rds  → delta −3 or −4  (major / minor 3rd)
 *   6ths  → delta +2
 *
 * shapeName controls the positional window:
 *   'SHORT SHAPE' — shows only the lowest-fret occurrence per string pair
 *                   (one compact position on the neck)
 *   'LONG SHAPE' or null — shows all occurrences across the full neck
 */
export function buildDoubleStopNodes(rootName, intervalName, tonality, stringFocus, shapeName) {
  const root = noteIndex(rootName);
  if (root === -1) return [];

  const formula    = tonality === 'minor'
    ? [0, 2, 3, 5, 7, 8, 10]
    : [0, 2, 4, 5, 7, 9, 11];
  const flats      = usesFlats(rootName, formula, tonality);
  const formulaSet = new Set(formula);
  const deltas     = DOUBLE_STOP_DELTAS[intervalName] ?? [0];

  // Pre-compute in-scale frets per string
  const inScale = MANDOLIN_TUNING.map((openNote, s) => {
    if (!stringFocus[s]) return new Set();
    const fretsSet = new Set();
    for (let f = 0; f <= TOTAL_FRETS_MANDOLIN; f++) {
      const interval = ((openNote + f) - root + 12 * 12) % 12;
      if (formulaSet.has(interval)) fretsSet.add(f);
    }
    return fretsSet;
  });

  // Collect all valid adjacent-string pairs
  const pairs = [];
  for (let s = 0; s < 3; s++) {
    if (!stringFocus[s] || !stringFocus[s + 1]) continue;
    for (const fLow of inScale[s]) {
      for (const delta of deltas) {
        const fHigh = fLow + delta;
        if (fHigh >= 0 && fHigh <= TOTAL_FRETS_MANDOLIN && inScale[s + 1].has(fHigh)) {
          pairs.push({ sLow: s, fLow, sHigh: s + 1, fHigh });
        }
      }
    }
  }

  // SHORT SHAPE: restrict to the lowest-fret occurrence per string pair
  // (one compact neck position rather than the full-neck pattern)
  let activePairs = pairs;
  if (shapeName === 'SHORT SHAPE') {
    const minFretPerPair = {};
    for (const { sLow, sHigh, fLow } of pairs) {
      const key = `${sLow}-${sHigh}`;
      if (minFretPerPair[key] === undefined || fLow < minFretPerPair[key]) {
        minFretPerPair[key] = fLow;
      }
    }
    activePairs = pairs.filter(({ sLow, sHigh, fLow }) =>
      fLow === minFretPerPair[`${sLow}-${sHigh}`]
    );
  }

  // Build node set from active pairs
  const activeKey = new Set();
  for (const { sLow, fLow, sHigh, fHigh } of activePairs) {
    activeKey.add(`${sLow}-${fLow}`);
    activeKey.add(`${sHigh}-${fHigh}`);
  }

  return Array.from(activeKey).map(k => {
    const [s, f] = k.split('-').map(Number);
    const pitch    = (MANDOLIN_TUNING[s] + f) % 12;
    const interval = (pitch - root + 12) % 12;
    return {
      string:   s,
      fret:     f,
      isRoot:   interval === 0,
      interval,
      noteName: getNoteName(pitch, flats),
    };
  });
}
